import React, {useEffect, useMemo, useRef, useState} from 'react';

export default function SearchableSelect({options, value, placeholder, error, onSelect, onClear, renderOption, renderPrefix, getLabel, filterInput, maxLength}) {
    const findSelected = () => {
        if (!value || !options.length) return null;
        return options.find(o => o.value === value)
            || options.find(o => o.value.toLowerCase() === value.toLowerCase())
            || null;
    };

    const selected = findSelected();

    const [query, setQuery] = useState(selected ? getLabel(selected) : '');
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            setQuery(selected ? getLabel(selected) : '');
        }
    }, [value, options.length]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        const isSelectedLabel = selected && q === getLabel(selected).toLowerCase();
        if (isSelectedLabel) return options;
        return options.filter(o => {
            const label = getLabel(o).toLowerCase();
            if (label.includes(q)) return true;
            if (o.name && o.name.toLowerCase().includes(q)) return true;
            return false;
        });
    }, [options, query, selected]);

    useEffect(() => {
        setHighlightedIndex(0);
    }, [query]);

    const handleSelect = (item) => {
        setQuery(getLabel(item));
        setIsOpen(false);
        setHighlightedIndex(0);
        onSelect(item.value, item);
    };

    const handleChange = (val) => {
        if (filterInput) val = filterInput(val);
        setQuery(val);
        setIsOpen(true);
        if (!val.trim() && onClear) onClear();
    };

    const handleBlur = () => {
        setTimeout(() => {
            setIsOpen(false);
            if (selected && query.trim().toLowerCase() === getLabel(selected).toLowerCase()) {
                return;
            }
            const exact = options.find(o => getLabel(o).toLowerCase() === query.trim().toLowerCase());
            if (exact) {
                handleSelect(exact);
            } else if (selected) {
                setQuery(getLabel(selected));
            } else {
                setQuery('');
            }
        }, 120);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setIsOpen(true);
            setHighlightedIndex(prev => Math.min(prev + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && isOpen && filtered[highlightedIndex]) {
            e.preventDefault();
            handleSelect(filtered[highlightedIndex]);
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    const toggle = () => {
        setIsOpen(prev => !prev);
        inputRef.current?.focus();
    };

    const hasPrefix = renderPrefix && selected;

    return (
        <div className={`resume-select-dropdown ${error ? 'error' : ''}`}>
            <div className={`resume-select-dropdown__shell ${hasPrefix ? 'resume-select-dropdown__shell--has-prefix' : ''}`}>
                {hasPrefix && (
                    <span className="resume-select-dropdown__prefix" onMouseDown={e => { e.preventDefault(); inputRef.current?.focus(); }}>
                        {renderPrefix(selected)}
                    </span>
                )}
                <input
                    ref={inputRef}
                    type="text"
                    inputMode={filterInput ? 'numeric' : 'text'}
                    maxLength={maxLength}
                    className={`form-control resume-select-dropdown__input ${hasPrefix ? 'resume-select-dropdown__input--prefixed' : ''}`}
                    value={query}
                    placeholder={placeholder || 'Select...'}
                    onChange={e => handleChange(e.target.value)}
                    onFocus={() => setIsOpen(true)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-autocomplete="list"
                />
                <button
                    type="button"
                    className={`resume-select-dropdown__toggle ${isOpen ? 'open' : ''}`}
                    onMouseDown={e => e.preventDefault()}
                    onClick={toggle}
                >
                    <span className={`resume-select-dropdown__chevron ${isOpen ? 'open' : ''}`} />
                </button>
            </div>
            {isOpen && filtered.length > 0 && (
                <div className="resume-select-dropdown__menu" role="listbox">
                    {filtered.map((item, index) => (
                        <button
                            key={item.value}
                            type="button"
                            className={`resume-select-dropdown__option ${highlightedIndex === index ? 'active' : ''} ${value === item.value ? 'selected' : ''}`}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => handleSelect(item)}
                        >
                            {renderOption ? renderOption(item) : getLabel(item)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
